import { useState } from "react";
import { getStrings, homeShellString } from "../i18n/strings";
import type { PublicIpRiskResultDto } from "../types/dto";
import { countryFlagEmoji } from "../utils/countryFlag";
import { netRiskScanBlurb } from "../utils/homeVerdict";
import { networkContextTypeLabel, resolveNetworkContext } from "../utils/riskExplanation";
import { resolveIndexBand, resolveNetRiskScanIndex } from "../utils/riskScore";
import { isPresentableValue } from "./HomeReportCard";
import { getScoreTone } from "../utils/scoreTone";
import { evidenceLevelLabel } from "./RiskSignalInsights";
import { trackEvent } from "../utils/telemetry";

type Strings = ReturnType<typeof getStrings>;

// The hero dashboard: one card, laid out as the reference design has it — a status line, then the
// address and network identity on the left with the NetRiskScan Index ring on the right, then a
// single tone-colored verdict strip, then a quiet footer carrying evidence quality and the two
// actions. One card, not a stack of cards (spec §七).
//
// This reads the same fields off the same PublicIpRiskResultDto through the same
// resolveNetRiskScanIndex/resolveIndexBand/getScoreTone/netRiskScanBlurb helpers HomeReportCard uses
// below it. There is no scoring logic here and there must never be: the direction of the Index
// (higher = cleaner) and the band edges belong to the backend (spec §十一/§三十).
//
// The band name appears exactly once, in the verdict strip: the ring states the number and what the
// number is called, not the band, so the card cannot end up showing "92 / Excellent / Low risk /
// Clean" as four competing readings of one result (spec §十二).
export function HomeVisitorIpCard({
  result,
  strings,
  helpPath,
  showAutoDetected,
  onRefresh,
  refreshing,
  onShare,
}: {
  result: PublicIpRiskResultDto;
  strings: Strings;
  helpPath: string;
  // True only for the visitor's own untouched auto-detect result (mirrors HomePage.tsx's !ipEdited —
  // a manually looked-up or ?ip=-supplied address has nothing left to claim about auto-detection,
  // and a *failed* auto-detect never reaches this component at all: there is no result to show a
  // card for, so that case is a standalone banner in HomePage.tsx instead).
  showAutoDetected: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  onShare: () => void;
}) {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);
  const [masked, setMasked] = useState(false);
  const [copied, setCopied] = useState(false);

  const flag = countryFlagEmoji(result.location.countryCode);
  // Providers sometimes fill these with a literal "Unknown"/"N/A" rather than leaving them null;
  // isPresentableValue treats those as absent so the identity line drops the fact entirely instead
  // of printing a placeholder next to the visitor's own address (spec §十).
  const countryName = isPresentableValue(result.location.countryName) ? result.location.countryName : null;
  const rawOrganization = result.network.isp ?? result.network.organization;
  const organization = isPresentableValue(rawOrganization) ? rawOrganization : null;
  const asn = isPresentableValue(result.network.asn) ? result.network.asn : null;
  const place = [result.location.cityName, result.location.regionName].filter(isPresentableValue).join(", ");

  const band = resolveIndexBand(result.risk) ?? "unknown";
  const tone = getScoreTone(band, "netriskscan");
  const score = resolveNetRiskScanIndex(result.risk);
  const levelLabel = strings.indexBandNames[band] ?? band;
  const assessmentGrade = result.risk.assessmentGrade ?? result.risk.assessmentQuality ?? "insufficient";

  async function copyIp() {
    await navigator.clipboard.writeText(result.meta.ipAddress);
    setCopied(true);
    trackEvent({ eventType: "CopyResult", pagePath: window.location.pathname });
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <section className="home-visitor-card" aria-labelledby="home-report-heading">
      {/* Reference design wants this line visible on success, not only on failure (a failed
          auto-detect has no result and never reaches this component — see HomePage.tsx's standalone
          banner for that case). */}
      {showAutoDetected && (
        <p className="home-visitor-card__status">
          <span className="home-visitor-card__dot" aria-hidden="true" />
          {t("homeYourIpDetected")}
        </p>
      )}

      <div className="home-visitor-card__grid">
        <div className="home-visitor-card__info">
          {/* Not a heading element: the home page's document outline shouldn't be titled with
              whatever address the visitor (or a crawler) happens to be arriving from — the page's
              one <h1> is the hero headline above this card. */}
          <p className="home-visitor-card__ip" id="home-report-heading">
            <span className={masked ? "is-masked" : undefined}>
              {masked ? maskAddress(result.meta.ipAddress) : result.meta.ipAddress}
            </span>
            <span className="home-visitor-card__ip-tools">
              <button
                type="button"
                className={`icon-button icon-button--bare${copied ? " is-copied" : ""}`}
                onClick={copyIp}
                aria-label={copied ? t("homeCopiedLabel") : t("homeCopyLabel")}
                title={copied ? t("homeCopiedLabel") : t("homeCopyLabel")}
              >
                <CopyIcon />
              </button>
              <button
                type="button"
                className="icon-button icon-button--bare"
                onClick={() => setMasked((value) => !value)}
                aria-label={t("homeMaskIpLabel")}
                title={t("homeMaskIpLabel")}
              >
                <EyeIcon crossed={masked} />
              </button>
            </span>
          </p>

          {/* Country / organization / AS number, each rendered only when the provider actually
              carried it. The AS number is a sibling of the organization rather than a child of it,
              so an address with an ASN but no ISP name still shows the ASN. */}
          {(countryName || organization || asn) && (
            <p className="home-visitor-card__identity">
              {countryName && (
                <span className="home-visitor-card__country">
                  {flag && <span className="home-visitor-card__flag" aria-hidden="true">{flag}</span>}
                  {countryName}
                </span>
              )}
              {organization && <span className="home-visitor-card__org">{organization}</span>}
              {asn && <span className="home-visitor-card__asn">{asn}</span>}
            </p>
          )}
          {place && <p className="home-visitor-card__place">{place}</p>}

          {/* Recognized shared infrastructure (a public DNS resolver, a CDN edge, a search-engine
              crawler) as one compact line, not the full explanatory banner: inside the hero card
              that banner pushed the Index ring and the verdict down by its own height on exactly
              the addresses that most need to stay readable (spec §十三). The full explanation is
              not lost — it renders in the light content area below, in HomeReportCard. */}
          <NetworkContextChip result={result} strings={strings} />
        </div>

        <div className="home-visitor-card__score">
          <IndexRing
            score={score}
            tone={tone}
            label={t("homeRiskLabel")}
            ariaLabel={`${t("homeRiskLabel")}: ${score === null ? "—" : score}, ${levelLabel}`}
          />
        </div>
      </div>

      {/* The one verdict: the band this Index falls in, plus the same plain-language reading of it
          that netRiskScanBlurb already produces. Colored by the same getScoreTone the ring uses, so
          the strip and the ring can never disagree. */}
      <div className={`home-verdict home-verdict--${tone}`}>
        <span className="home-verdict__label">
          <ShieldIcon />
          {levelLabel}
        </span>
        <span className="home-verdict__text">{netRiskScanBlurb(band, strings)}</span>
      </div>

      <div className="home-visitor-card__foot">
        <p className="home-visitor-card__meta">
          <span>{strings.assessmentGradeLabel}: {strings.assessmentGradeNames[assessmentGrade] ?? assessmentGrade}</span>
          {/* evidence-quality-v1: the same Coverage/Agreement/Confidence the full result page's
              EvidenceQualityPanel shows, compressed into one quiet inline fact — this footer is
              already documented as "carrying evidence quality" (see this component's own header
              comment), and until now it never actually did. Absent entirely on a pre-evidence-
              quality-v1 snapshot (evidenceConfidence undefined). */}
          {result.risk.evidenceConfidence && (
            <span>
              {strings.evidenceQualityCoverage ?? "Coverage"} {result.risk.coveragePercent}% ·{" "}
              {strings.evidenceQualityAgreement ?? "Agreement"} {evidenceLevelLabel(result.risk.evidenceAgreement, strings)} ·{" "}
              {strings.evidenceQualityConfidence ?? "Confidence"} {evidenceLevelLabel(result.risk.evidenceConfidence, strings)}
            </span>
          )}
          <a href={helpPath}>{t("homeScoreHelpLabel")}</a>
        </p>
        <div className="home-visitor-card__actions">
          {/* Only offered for the visitor's own auto-detected address — re-running the shared
              my-ip query would be meaningless next to a manually looked-up result. */}
          {showAutoDetected && (
            <button type="button" className="home-action" onClick={onRefresh} disabled={refreshing}>
              <RefreshIcon />
              {refreshing ? strings.loading : t("homeRefreshLabel")}
            </button>
          )}
          <button type="button" className="home-action home-action--accent" onClick={onShare}>
            <ShareIcon />
            {t("homeShareLabel")}
          </button>
        </div>
      </div>
    </section>
  );
}

// One line, one fact: what kind of shared infrastructure this address was recognized as, and whose.
// Reuses the same resolveNetworkContext/networkContextTypeLabel helpers and the same translated
// networkContextBannerTitle template NetworkContextBanner uses, so the compact form and the full
// banner below can never name the address two different things.
function NetworkContextChip({ result, strings }: { result: PublicIpRiskResultDto; strings: Strings }) {
  const context = resolveNetworkContext(result.risk);
  if (!context.isRecognized) return null;

  const typeLabel = networkContextTypeLabel(context.type, strings);
  const title = (strings.networkContextBannerTitle ?? "Recognized public infrastructure: {type}").replace("{type}", typeLabel);
  const organization = isPresentableValue(context.organization) ? context.organization : null;

  return (
    <p className="home-visitor-card__context">
      <InfoIcon />
      <span>
        {title}
        {organization && <span className="home-visitor-card__context-org"> · {organization}</span>}
      </span>
    </p>
  );
}

// Circular NetRiskScan Index gauge. Same direction convention as everywhere else in the app: 0 (high
// risk) reads as an empty ring, 100 (clean) as a full one — SVG only, no chart library (spec §三十三).
const RING_SIZE = 140;
const RING_STROKE = 9;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function IndexRing({
  score,
  tone,
  label,
  ariaLabel,
}: {
  score: number | null;
  tone: "safe" | "warning" | "danger" | "neutral";
  label: string;
  ariaLabel: string;
}) {
  const pct = score === null ? 0 : Math.max(0, Math.min(100, score));
  const offset = RING_CIRCUMFERENCE * (1 - pct / 100);

  return (
    <div className="home-ring" role="img" aria-label={ariaLabel}>
      <svg viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} width={RING_SIZE} height={RING_SIZE} aria-hidden="true">
        <circle
          className="home-ring__track"
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
          fill="none"
        />
        {score !== null && (
          <circle
            className={`home-ring__value home-ring__value--${tone}`}
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            strokeWidth={RING_STROKE}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          />
        )}
      </svg>
      <div className="home-ring__center">
        <strong className={`home-ring__score home-ring__score--${tone}`}>{score === null ? "—" : score}</strong>
        <span className="home-ring__label">{label}</span>
      </div>
    </div>
  );
}

// Only the last segment of an IPv4 host part (or an IPv6 tail) survives — enough to recognise your
// own address in a screenshot, not enough to publish it.
function maskAddress(address: string): string {
  const separator = address.includes(":") ? ":" : ".";
  const parts = address.split(separator);
  if (parts.length < 2) return "•••";
  return parts.map((part, index) => (index < parts.length - 1 ? "•".repeat(Math.max(part.length, 1)) : part)).join(separator);
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
      {crossed && <path d="m4 20 16-16" />}
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="13" height="13" x="9" y="9" rx="2" />
      <path d="M5 15c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 11a8 8 0 1 0-.9 4.6" />
      <path d="M20 5v6h-6" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}
